/**
 * @file Manual Match related actions.
 */
import pMap from 'p-map-browser';

import { LeaseSelectedIdentity } from '../../graphql/queries/votes';
import { getErrorString } from '../../../../util/normalize-errors';
import getLogger from '../../../../util/logger';
import { MANUAL_MATCH_LEASE_TIME } from '../../constants/command';

const log = getLogger('ManualMatchActions');

export const MANUAL_MATCH_FETCH_SELECTED_IDENTITY_REQUEST =
  'manualMatch/MANUAL_MATCH_FETCH_SELECTED_IDENTITY_REQUEST';
export const MANUAL_MATCH_FETCH_SELECTED_IDENTITY_SUCCESS =
  'manualMatch/MANUAL_MATCH_FETCH_SELECTED_IDENTITY_SUCCESS';
export const MANUAL_MATCH_VOTES_CAST_REQUEST =
  'manualMatch/MANUAL_MATCH_VOTES_CAST_REQUEST';
export const MANUAL_MATCH_VOTES_CAST_SUCCESS =
  'manualMatch/MANUAL_MATCH_VOTES_CAST_SUCCESS';
export const MANUAL_MATCH_VOTES_CAST_FAILURE =
  'manualMatch/MANUAL_MATCH_VOTES_CAST_FAILURE';
export const MANUAL_MATCH_SELECT_PHOTOS =
  'manualMatch/MANUAL_MATCH_SELECT_PHOTOS';
export const MANUAL_MATCH_DESELECT_PHOTOS =
  'manualMatch/MANUAL_MATCH_DESELECT_PHOTOS';
export const MANUAL_MATCH_NEXT_VOTE = 'manualMatch/MANUAL_MATCH_NEXT_VOTE';

const fetchSelectedIdentityRequest = () => ({
  type: MANUAL_MATCH_FETCH_SELECTED_IDENTITY_REQUEST,
});

const fetchSelectedIdentitySuccess = lease => ({
  type: MANUAL_MATCH_FETCH_SELECTED_IDENTITY_SUCCESS,
  lease,
});

const castManualVoteVotesRequest = () => ({
  type: MANUAL_MATCH_VOTES_CAST_REQUEST,
});

const castManualVoteVotesSuccess = (photos, vote) => ({
  type: MANUAL_MATCH_VOTES_CAST_SUCCESS,
  vote,
  photos,
});

const castManualVoteVotesFailure = error => ({
  type: MANUAL_MATCH_VOTES_CAST_FAILURE,
  error,
});

export const manualMatchSelectPhotos = photos => ({
  type: MANUAL_MATCH_SELECT_PHOTOS,
  photos,
});

export const manualMatchDeselectPhotos = photos => ({
  type: MANUAL_MATCH_DESELECT_PHOTOS,
  photos,
});

export const manualMatchCastVotes = ({
  selectedIdentity,
  photos,
  vote,
  pubBoxCreateMutation,
  pubBoxVoteMutation,
  pubBox,
}) => async dispatch => {
  log.info('manualMatchCastVotes :: photos Cast: ', photos);
  log.info('manualMatchCastVotes :: vote: ', vote);

  dispatch(castManualVoteVotesRequest());
  try {
    const pubBoxesIds = await pMap(
      photos,
      async photo => {
        if (pubBox) {
          return pubBox.id;
        }

        try {
          const {
            data: {
              humanLabeledPubBoxCreate: { pub_box: createdPubBox },
            },
          } = await pubBoxCreateMutation({
            photoId: photo.id,
            box: [0.4975, 0.4975, 0.005, 0.005],
          });

          return createdPubBox.id;
        } catch (error) {
          log.error(error);
          return undefined;
        }
      },
      { concurrency: 3 },
    );

    log.info('manualMatchCastVotes :: Votes: ', pubBoxesIds);

    await pubBoxVoteMutation(
      selectedIdentity.identity.id,
      pubBoxesIds.filter(id => !!id),
      vote,
    );

    dispatch(castManualVoteVotesSuccess(photos, vote));
  } catch (error) {
    log.error('manualMatchCastVotes :: error', error);
    castManualVoteVotesFailure(getErrorString(error));
  }
};

export const manualMatchCastFaceboxVotes = ({
  selectedIdentity,
  selfie,
  photo,
  face,
  vote,
  user,
  shouldUpdateSelfie,
  bulkVoteMutation,
  updateSelfieMutation,
}) => async dispatch => {
  log.info('manualMatchCastFaceboxVotes :: called with: ', {
    vote,
    selfie,
    selectedIdentity,
    photo,
    face,
    user,
    shouldUpdateSelfie,
  });

  dispatch(castManualVoteVotesRequest());
  try {
    await bulkVoteMutation({
      selectedIdentityId: selectedIdentity.id,
      comparedPhotoId: photo.id,
      comparedFaceId: face.id,
      decision: vote,
      selfieFaceId: selfie.id,
      userId: user.id,
    });

    if (shouldUpdateSelfie) {
      await updateSelfieMutation({
        photoId: photo.id,
        faceId: face.id,
        identityId: selectedIdentity.identity.id,
      });
    }

    dispatch(castManualVoteVotesSuccess([photo], vote));
  } catch (error) {
    log.error('manualMatchCastVotes :: error', error);
    castManualVoteVotesFailure(getErrorString(error));
  }
};

/**
 * Fetches & leases a selected identity for manual matching.
 *
 * @param {Object} opts Object of fetch identity options.
 * @param {Object} opts.client Apollo client object.
 * @param {Object} opts.selectedIdentityId Selected Identity to fetch.
 * @param {Object?} opts.faceMatchVoteId Optional faceMatchVoteId to start from..
 *
 */
export const fetchSelectedIdentity = ({
  client,
  selectedIdentityId,
  faceMatchVoteId,
}) => async dispatch => {
  dispatch(fetchSelectedIdentityRequest());

  const {
    data: { leaseSelectedIdentity },
  } = await client.query({
    query: LeaseSelectedIdentity,
    variables: {
      time: MANUAL_MATCH_LEASE_TIME,
      selectedIdentityId,
    },
    fetchPolicy: 'network-only',
  });

  const selectedIdentity = leaseSelectedIdentity.selected_identity;
  const faceMatchVotes = selectedIdentity?.face_match_votes?.records ?? [];
  const activeFaceMatchVote = faceMatchVoteId
    ? faceMatchVotes.find(({ id }) => id === faceMatchVoteId)
    : undefined;
  const activeFaceMatchVoteIndex = activeFaceMatchVote
    ? faceMatchVotes.indexOf(activeFaceMatchVote)
    : 0;

  dispatch(
    fetchSelectedIdentitySuccess({
      lease_id: leaseSelectedIdentity.lease_id,
      expiresAt: leaseSelectedIdentity.lease_expires_at,
      selectedIdentity,
      activeFaceMatchVoteIndex,
    }),
  );
};

export const manualMatchNextVote = () => ({
  type: MANUAL_MATCH_NEXT_VOTE,
});
